<script setup lang="ts">
import { BillingInfo } from '@/@types'
import { Cart, Coupon } from '@/@types/cart'
import Resume from '@/components/shared/cart/Resume.vue'
import { CartComponent, OrderSummaryComponent, PaymentMethodComponent } from '@/components/shared/cart/steps'
import BillingData from '@/components/shared/cart/steps/billing-data/BillingData.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Stepper, StepperIndicator, StepperItem, StepperTitle } from '@/components/ui/stepper'
import { Title } from '@/components/ui/title'
import { cartTotalsKeys, useCartTotals } from '@/composables/useCartTotals'
import { checkoutStoreKeys, useCheckout } from '@/composables/useCheckout'
import LightLayout from '@/layouts/LightLayout.vue'

import { Head, Link, router } from '@inertiajs/vue3'

import { Check } from 'lucide-vue-next'

import { computed, provide, watch } from 'vue'

const {
    current_step,
    agreed: agreedProp = false,
    billing_info,
    cart,
    coupon,
    control_step
} = defineProps<{
    cart: Cart
    coupon?: Coupon
    current_step: number
    agreed?: boolean
    final_total: number
    billing_info?: BillingInfo
    control_step?: boolean
}>()

const steps = [
    { step: 1, title: 'Your tickets' },
    { step: 2, title: 'Payment method' },
    { step: 3, title: 'Billing data' },
    { step: 4, title: 'Purchase Summary' },
]

const lastStep = steps.length
const cartItems = computed(() => cart.items)
const activeCoupon = computed(() => coupon)

const cartTotals = useCartTotals(cartItems, activeCoupon)

provide(cartTotalsKeys, cartTotals)

const checkoutStore = useCheckout({
    current_step,
    billing_info,
    agreed: agreedProp,
    cartTotals,
    coupon: activeCoupon.value?.code,
    control_step
})

provide(checkoutStoreKeys, checkoutStore)

const { currentStep, agreed, controlStep, selectedPaymentMethod, form, handleSubmit } = checkoutStore

export type FormSchema = typeof form

provide('stepControl', {
    currentStep,
    agreed,
    controlStep,
})
provide('selectedPaymentMethod', selectedPaymentMethod)

watch(
    () => activeCoupon.value,
    () => {
        if (activeCoupon.value) {
            form.coupon_code = activeCoupon.value.code
        }
    },
)

watch(
    () => cartTotals.totalQuantity.value,
    () => {
        if (cartTotals.totalQuantity.value >= 5) {
            form.coupon_code = ''
        }
    },
    {
        immediate: true,
    },
)
</script>

<template>
    <Head :title="$t('cart.title')"></Head>

    <LightLayout :hideFooter="true">
        <div v-if="!cart.items.length" class="container flex flex-col gap-10">
            <Card>
                <CardContent class="flex flex-col gap-10 p-8">
                    <Title class="!text-2xl">{{ $t('Your cart is empty') }}</Title>

                    <Button
                        variant="outline"
                        class="text-primary border-primary hover:bg-primary hover:text-primary-foreground h-[48px] w-fit text-sm"
                        :as="Link"
                        href="/#tickets"
                    >
                        {{ $t('Keep shopping') }}
                    </Button>
                </CardContent>
            </Card>
        </div>

        <div v-else class="container flex flex-col gap-10 overflow-visible lg:flex-row">
            <div class="flex flex-1 flex-col gap-8">
                <Title class="!text-3xl">{{ currentStep === lastStep ? $t('Cart') : $t('Finalize purchase') }}</Title>

                <Stepper v-model="currentStep" class="font-inter flex w-full flex-col items-start gap-2 overflow-x-auto lg:flex-row">
                    <StepperItem v-for="step in steps" :key="step.step" v-slot="{ state }" class="flex w-fit first:pl-0 lg:px-2" :step="step.step">
                        <StepperIndicator
                            @click="
                                () => {
                                    if (step.step === 1) {
                                        router.get(
                                            '/cart',
                                            {},
                                            {
                                                preserveState: true,
                                            },
                                        )
                                    }

                                    if (state === 'completed') {
                                        currentStep = cartTotals.total.value <= 0 ? 1 : step.step
                                        controlStep = true
                                    }
                                }
                            "
                            class="group-data-[state=completed]:bg-primary group-data-[state=active]:bg-tranparent group-data-[state=active]:border-primary group-data-[state=active]:text-primary h-[40px] w-[40px] group-data-[state=active]:border group-data-[state=active]:border-dashed group-data-[state=completed]:text-white group-data-[state=inactive]:border"
                        >
                            <Check v-if="state === 'completed'" />
                            <span v-if="state != 'completed'">{{ String(step.step).padStart(2, '0') }}</span>
                        </StepperIndicator>
                        <StepperTitle
                            class="group-data-[state=active]:text-primary text-sm font-medium text-gray-900 group-data-[state=inactive]:text-slate-400"
                        >
                            {{ $t(step.title) }}
                        </StepperTitle>
                    </StepperItem>
                </Stepper>
                <CartComponent :cart="cart" v-if="currentStep === 1" />
                <form @submit.prevent="handleSubmit" id="form">
                    <PaymentMethodComponent v-if="currentStep === 2" :form="form" />
                    <BillingData v-if="currentStep === 3" :form="form" />
                    <OrderSummaryComponent v-if="currentStep === 4" :cartItems="cart.items" :coupon="coupon" :form="form" />
                </form>
            </div>

            <Resume :cartItems="cart.items" :coupon="coupon" :form="form" />
        </div>
    </LightLayout>
</template>
